import React, { useEffect, useMemo } from 'react'

import { filter, head } from 'ramda'
import { useHistory, useLocation } from 'react-router-dom'

import { FeatureBadge, Flex, styled } from '@driftt/dds'
import { Breadcrumbs, NavigationHeader } from '@driftt/tide-core'

import AutomationFeedbackButton from 'components/automation/AutomationFeedbackButton'
import DateRangePicker from 'components/filters/DateRangePicker'
import config from 'config'
import useFilters from 'hooks/useFilters'
import useHasCoverageReportAccess from 'hooks/useHasCoverageReportAccess'
import { getFilterQueryString } from 'modules/filter/utils'
import { isCmdClickEvent } from 'utils/event'

import './style.css'

interface INavItem {
  label: string | React.ReactNode
  route: string
  parentRoute?: any[]
}

const StyledDiv = styled('div', {
  display: 'flex',
  alignItems: 'center',
})

const AIConversationInsightsLabel = () => (
  <StyledDiv>
    Conversation insights
    <FeatureBadge variant="new" css={{ marginLeft: '$1' }} />
  </StyledDiv>
)

const AIPerformanceOverviewLabel = () => (
  <StyledDiv>
    AI overview
    <FeatureBadge variant="new" css={{ marginLeft: '$1' }} />
  </StyledDiv>
)

const AppNavigationHeader: React.FC<{
  showDatePicker?: boolean
  extraContent?: React.ReactNode | null
  datePickerExtraContent?: React.ReactNode | null
  hasTabs?: boolean
}> = (props) => {
  const { timePeriod } = useFilters()
  const { showDatePicker, extraContent, datePickerExtraContent, hasTabs } =
    props
  const { pathname } = useLocation()
  const history = useHistory()

  const hasCoverageReportAccess = useHasCoverageReportAccess()

  const queryString = getFilterQueryString(timePeriod)

  const navItems: INavItem[] = useMemo(() => {
    const items = [
      {
        label: 'Chat overview',
        route: '/conversations',
      },
      {
        label: <AIConversationInsightsLabel />,
        route: '/ai-performance/conversation-insights',
      },
      {
        label: <AIConversationInsightsLabel />,
        route: '/ai-performance/conversation-insights/categories',
        parentRoute: [
          {
            label: 'Conversation insights',
            route: '/ai-performance/conversation-insights',
          },
        ],
      },
      {
        label: <AIConversationInsightsLabel />,
        route: '/ai-performance/conversation-insights/keywords',
        parentRoute: [
          {
            label: 'Conversation insights',
            route: '/ai-performance/conversation-insights',
          },
        ],
      },
      {
        label: <AIPerformanceOverviewLabel />,
        route: '/ai-performance/overview',
      },
      {
        label: 'Meetings',
        route: '/meetings',
      },
      {
        label: 'Meetings',
        route: '/meetings/time',
        parentRoute: [
          {
            label: 'Meetings',
            route: '/meetings',
          },
        ],
      },
      {
        label: 'Meetings',
        route: '/meetings/teammate',
        parentRoute: [
          {
            label: 'Meetings',
            route: '/meetings',
          },
        ],
      },
      {
        label: 'Calls',
        route: '/calls',
      },
      {
        label: 'Calls',
        route: '/calls/time',
        parentRoute: [
          {
            label: 'Calls',
            route: '/calls',
          },
        ],
      },
      {
        label: 'Calls',
        route: '/calls/teammate',
        parentRoute: [
          {
            label: 'Calls',
            route: '/calls',
          },
        ],
      },
      {
        label: 'Chat playbooks',
        route: '/playbooks/chat',
        parentRoute: hasCoverageReportAccess
          ? [
              {
                label: 'Chat playbooks',
                route: '/playbooks/chat',
              },
            ]
          : [],
      },
      {
        label: 'Missed conversations',
        route: '/conversations/missed',
      },
      {
        label: 'Missed conversations by day',
        route: '/conversations/missed/day',
        parentRoute: [
          {
            label: 'Missed conversations',
            route: '/conversations/missed',
          },
        ],
      },
      {
        label: 'Missed conversations by teammate',
        route: '/conversations/missed/teammate',
        parentRoute: [
          {
            label: 'Missed conversations',
            route: '/conversations/missed',
          },
        ],
      },
      {
        label: 'Email playbooks',
        route: '/playbooks/email',
      },
      {
        label: 'Team performance',
        route: '/team',
      },
      {
        label: 'Routed chats',
        route: '/routing',
      },
      {
        label: 'Automation',
        route: '/automation',
      },
      {
        label: 'Email campaigns',
        route: '/email/campaigns',
      },
      {
        label: 'Real-time dashboard',
        route: '/real-time',
      },
      {
        label: 'Chat data',
        route: '/conversations/data',
      },
      {
        label: 'Opportunity overview',
        route: '/revenue/overview',
      },
      {
        label: 'Opportunity data',
        route: '/revenue/opportunities',
      },
      {
        label: 'Content recommendations',
        route: '/siteconcierge/content',
      },
      {
        label: 'Intelligent search',
        route: '/siteconcierge/search',
      },
      {
        label: 'Site concierge',
        route: '/site-concierge',
      },
      {
        label: 'Site concierge',
        route: '/site-concierge/overview',
        parentRoute: [
          {
            label: 'Site concierge',
            route: '/site-concierge',
          },
        ],
      },
      {
        label: 'Site concierge',
        route: '/site-concierge/meetings',
        parentRoute: [
          {
            label: 'Site concierge',
            route: '/site-concierge',
          },
        ],
      },
      {
        label: 'Site concierge',
        route: '/site-concierge/search',
        parentRoute: [
          {
            label: 'Site concierge',
            route: '/site-concierge',
          },
        ],
      },
      {
        label: 'Site concierge',
        route: '/site-concierge/content',
        parentRoute: [
          {
            label: 'Site concierge',
            route: '/site-concierge',
          },
        ],
      },
      {
        label: 'Intelligent search',
        route: '/intelligent-search',
        parentRoute: [
          {
            label: 'Intelligent search',
            route: '/intelligent-search',
          },
        ],
      },
    ]

    if (hasCoverageReportAccess) {
      items.push({
        label: 'Site coverage',
        route: '/playbooks/chat/site-coverage',
        parentRoute: [
          {
            label: 'Chat playbooks',
            route: '/playbooks/chat',
          },
        ],
      })
    }

    return items
  }, [hasCoverageReportAccess])

  const navItem = useMemo(() => {
    const navItem = head(
      filter((item: INavItem) => item.route === pathname)(navItems)
    )
    return navItem ? navItem : { label: 'Reports', route: '' }
  }, [pathname, navItems])

  const breadcrumbsHeadings = useMemo(() => {
    const breadcrumbs = [
      {
        title: 'Reports',
        link: config.APP_ROOT + queryString,
      },
    ]

    if (navItem.parentRoute) {
      const additionalCrumbs = navItem.parentRoute.reduce(
        (crumbs, item) => {
          crumbs.unshift({
            title: item.label,
            link: config.APP_ROOT + item.route + queryString,
          })

          return crumbs
        },
        [
          {
            title: navItem.label,
            link: config.APP_ROOT + navItem.route + queryString,
            isActive: true,
          },
        ]
      )
      return [...breadcrumbs, ...additionalCrumbs]
    } else {
      return [
        ...breadcrumbs,
        {
          title: navItem.label,
          link: config.APP_ROOT + navItem.route + queryString,
          isActive: true,
        },
      ]
    }
  }, [navItem, queryString])

  const onHeadingClick = (heading, event) => {
    if (isCmdClickEvent(event)) {
      return true
    }
    event.preventDefault()
    history.push(heading.link.replace(config.APP_ROOT, ''))
    return false
  }

  // Custom styles to support reports with tabs
  useEffect(() => {
    const header = document.querySelector('#reports-body > div > header')
    if (hasTabs && header) {
      header.classList.add('reports--navigation-header-with-tabs')
    }
    return () => {
      if (header) {
        header.classList.remove('reports--navigation-header-with-tabs')
      }
    }
  }, [hasTabs])

  return (
    <NavigationHeader
      title={navItem.label}
      history={history}
      secondary={
        <Breadcrumbs
          onHeadingClick={onHeadingClick}
          headings={breadcrumbsHeadings}
        />
      }
      className="reports--navigation-header"
    >
      <Flex alignItems="center">
        {extraContent}
        {showDatePicker && <DateRangePicker anchorDirection="right" />}
        {navItem.label === 'Automation' && <AutomationFeedbackButton />}
        {!!datePickerExtraContent && datePickerExtraContent}
      </Flex>
    </NavigationHeader>
  )
}

export default AppNavigationHeader
